var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { AwsUi } from '../../../core/runtime/src';
import { findUpUntil, KeyCode } from '../../../core/utils/src/dom';
import { Icon } from '../../icon/src';
import { Spinner } from '../../spinner/src';
import { ComponentFeature } from './internal';
import Controller from './controller';
import { StickyHandler, StickyState } from './base-sticky-header';
import StickyHeaderHandler, { stickyActiveClass } from './table-sticky-header';
import ResizableColumnsHandler from './resizable-columns/handler';
import { RESIZABLE_COLUMNS_CLASS, RESIZABLE_COLUMNS_RESIZER_CLASS, RESIZABLE_COLUMNS_TRACKER_CLASS } from './resizable-columns/constants';
import { positionScrollbar } from './sticky-scrollbar/utils';
import { arrayItemsEqual, getComponentSetter, updateController, checkReadinessAndInitializeController } from './utility';
import * as utilities from './componentsUtils';
import SelectionControl from './selection/selection-control';
import LegacyClassBuilder from '../../../core/utils/src/class-builders/legacy';
import { syncedHandlers } from '../../../core/utils/src/sync-scroll/sync-scroll';
export * from './pagination/index';
export * from './sorting/index';
export * from './filtering/index';
export * from './property-filtering/index';
export * from './selection/index';
export * from './preferences/index';
var SMALL_BREAKPOINT = 768;
var lastGeneratedName = 0;
var nextGeneratedName = function () { return "awsui-table-" + lastGeneratedName++; };
export var Table = AwsUi.__addComponent('awsui-table', {
    releaseStatus: 'stable',
    events: {
        // Fired when the list of items that are visible in the table changes upon user interaction.
        // Note that the initial render or a programmatic items change will not trigger the event.
        // If you need to cover these cases too, consider filtering and paginating items manually on your side.
        // The event `detail` contains the list of the visible items.
        visibleItemsChange: {
            bubbles: true,
            cancelable: false,
            detailType: {
                type: 'object',
                objectType: 'Table.VisibleItemsChangeDetail',
                itemType: 'Item'
            }
        },
        // Fired when the user decided to wrap lines in table cells.
        // This property comes from the table-wrap-lines component.
        wrapLinesChange: {
            bubbles: true,
            cancelable: false,
            detailType: 'Table.WrapLinesChangeDetail'
        },
        // Fired when user decided to change visible content in the table.
        // Event detail contain an array of columns ids that the user decided to include in the view.
        // This property comes form the content-selection component.
        contentSelectionChange: {
            bubbles: true,
            cancelable: false,
            detailType: 'Table.ContentSelectionChangeDetail'
        },
        // Fired when the user resized a table column. The event detail contains an array of column
        // widths in pixels. Use this event to persist the column widths. The table component does
        // not store this information across re-rendering operations.
        columnWidthsChange: {
            bubbles: true,
            cancelable: false,
            detailType: 'Table.ColumnWidthsChangeDetail'
        },
        // Fired when updating table properties and before the sticky header state is reset.
        // Canceling this event will prevent table from scrolling to then top and resetting sticky header state.
        beforeUnstick: {
            bubbles: true,
            cancelable: true
        },
        // **Important** This feature is for consoles migrating to Polaris to preserve functionality
        // of their deprecated console that customers expect in the replacement console. It is not meant to
        // add new functionality or support new use cases. This feature will be deprecated in the future when
        // it is no longer needed.
        //
        // Fired when the user clicked at a table row. The event detail contains the index of the
        // clicked row and the row object itself. Use this event to define a row click behavior
        rowClick: {
            bubbles: true,
            cancelable: false,
            detailType: {
                type: 'object',
                objectType: 'Table.RowClickDetail',
                itemType: 'Item'
            }
        },
        // **Important** This feature is for consoles migrating to Polaris to preserve functionality
        // of their deprecated console that customers expect in the replacement console. It is not meant to
        // add new functionality or support new use cases. This feature will be deprecated in the future when
        // it is no longer needed.
        //
        // Fired when the user clicked at a table row with the right mouse click. The event detail
        // contains the index of the clicked row and the row object itself. Use this event to override
        // the default browser context menu behavior.
        rowContextMenu: {
            bubbles: true,
            cancelable: true,
            detailType: {
                type: 'object',
                objectType: 'Table.RowContextMenuDetail',
                itemType: 'Item'
            }
        }
    },
    elementQueries: [
        {
            read: {
                selector: '.awsui-table-inner',
                properties: ['width']
            },
            apply: function (_a) {
                var width = _a.width;
                this.__innerSizeClass = parseInt(width, 10) <= SMALL_BREAKPOINT ? 'awsui-table-inner-small' : '';
                this.__stickyHandler.handleContainerChange();
                this.__controller.checkContainerScrolling();
                if (this.resizableColumns) {
                    this.__resizableColumnHandler.onContainerChange(width);
                }
                this.__updateScrollbarPosition();
            }
        }
    ],
    regions: {
        // Use this region to add filtering, pagination, selection, sorting and preferences.
        tools: {
            isDefault: true
        },
        // Displayed on top of the tools region. Use it for the heading and the action stripe.
        header: {},
        // Displayed only when the list of items is empty.
        empty: {},
        // Displayed only when all available items are filtered out by the filtering component.
        noMatch: {}
    },
    generics: {
        Item: {
            defaultType: 'Table.Item'
        }
    },
    properties: {
        // The columns configuration object
        // * `id` [string]: unique column identifier.
        //   The `id` property is needed only if the `awsui-table-sorting` or the `awsui-table-content-selector` components
        //   are used or the `resizableColumns` property is set to true.
        // * `header` [Renderable]: responsible for the display of the column header. The property value should provide
        //    a renderable value. The definition of "renderable" depends on your framework, see below.
        // * `cell` [Renderable]: responsible for the display of a cell content. The value should be renderable. You
        //    will receive a current table row item into your renderable context. The definition of "renderable" depends
        //    on your framework, see below.
        // * `label` [`SortingState => string`]: optional function that can be called to provide
        //   an `aria-label` for the cell header. It receives the current sorting state in the form of an object.
        //   Currently supported properties are `sortingColumn`, `sortingDescending`, and `disabled`.
        //   We recommend to at least use this for the sortable columns to provide more meaningful labels based on the
        //   current sorting direction.
        // * `width` [integer]: column width in pixels. If the width is not set, the browser will automatically adjust the
        //   column width based on the content. However, if `resizableColumns` is set to true, a default width of "120px"
        //   will be applied to columns that are not initially visible and don't have an explicit width property specified.
        //   The last column always fills the remaining space of the table, therefore, its specified width will be ignored.
        //   If you provide this value, we *strongly* recommend that you store the value in state, and keep it updated when
        //   columns are resized by listening to the \`columnWidthsChange\` event, otherwise the component may behave
        //   inconsistently.
        // * `minWidth` [string]: minimum column width. Use it combination with `resizableColumns` property set to true in
        //   order to specify the minimum width the user can resize to (defaults to "120px"). You can also use it in
        //   combination with the `wrapLines` property set to false to specify the minimum width of truncated text.
        //   Only pixels are supported. *For example: "54px"*.
        // * `allowLineWrap` [boolean]: if you are using the `awsui-table-wrap-lines`component,
        //   you need to set this property to `true` in order to explicitly state that the content of the column can
        //   be truncated. In order for this setting to be effective, you also need to specify the `minWidth`:
        //   content truncation will always guarantee that a certain part of it will always be visible.
        //
        // **Renderable value**
        //   * **AngularJS:** Must be a string representing an Angular template. For table cell template you can also use
        //     local scope variable `$item`. **Security note:** the return value will be treated as HTML, make sure you have
        //     escaped the user input using [ngBind](https://docs.angularjs.org/api/ng/directive/ngBind) or [interpolation](https://docs.angularjs.org/guide/interpolation).
        //     Example: `'<a>{{$item.prop}}</a>'`.
        //   * **React:** Must be a valid React element or a function that returns a React element to display. For table
        //     cells, the function will receive the table item as an argument.
        //     Example: `item => {return (<a>item.prop</a>);}` or just `<b>Header<b>` in jsx.
        //   * **GWT:** Must be a lambda function that should return either a string, html element or GWT-widget instance.
        //     Example: `() -> 'some text'` or `() -> new HTMLPanel()`.
        columnDefinitions: {
            type: 'array',
            itemTypes: [
                {
                    type: 'object',
                    objectType: 'Table.ColumnDefinition',
                    itemType: 'Item'
                }
            ],
            onChange: function (oldValue, newValue) {
                updateController('columnDefinitions').call(this, oldValue, newValue);
                // When the columnDefinitions changed, width or min-width of the columns might
                // be modified, therefore, the new table width needs to be re-calculated.
                if (this.resizableColumns) {
                    this.__resizableColumnHandler.recalculateTableWidth();
                }
            }
        },
        // The items serve as data source for a row.
        // The display of a row is handled by the `cell` property of each column definition in
        // the `columnDefinitions` property.
        //
        // _Remark:_ if you want to change the set of items make sure you provide a new array.
        // Any modification to the same array will not be reflected in the view.
        items: {
            type: 'array',
            itemTypes: ['Item'],
            onChange: updateController('items')
        },
        // Renders the table as being in a loading state.
        // It is highly recommended to also set a `loadingText`.
        loading: {
            type: 'boolean',
            onChange: updateController('loading')
        },
        // Text to be displayed when in loading state.
        loadingText: {
            type: 'string'
        },
        // List of the sub-components that will be present in the table default region. All the presented
        // sub-components need to be specified, otherwise, the table rows will not be rendered.
        // The list can contain `filtering`, `pagination`, `selection`, `sorting`, and `propertyFiltering`.
        // Example: `['pagination', 'filtering', 'selection']`.
        //
        // **GWT, React 16:** For performance reasons, **this property is mandatory
        // if you are using GWT or React 16**. In Angular, you can leave it undefined.
        features: {
            type: 'array',
            itemTypes: ['string'],
            defaultValue: [],
            onChange: function () {
                this.__checkReadinessAndInitializeController();
            }
        },
        // Set this to true if you want the table header to remain visible when the user scrolls down
        stickyHeader: {
            type: 'boolean',
            onChange: function (oldValue, newValue) {
                this.__updateStickySetting(newValue);
            }
        },
        // Optionally provide a vertical offset (in pixels) for the sticky header,
        // for example if you need to position the sticky header below other fixed position elements on the page.
        stickyHeaderVerticalOffset: {
            type: 'integer',
            onChange: function (oldValue, newValue) {
                this.__updateStickyVerticalOffset(newValue);
            }
        },
        // If set to true, it will cause the table to wrap lines as opposed to using ellipsis.
        wrapLines: {
            type: 'boolean',
            defaultValue: true,
            onChange: function (oldValue, newValue) {
                this.__updateWrapLines(newValue);
            }
        },
        // Set it to true if you would like to allow resizable columns on the table.
        resizableColumns: {
            type: 'boolean',
            defaultValue: false,
            onChange: function (oldValue, newValue) {
                this.__updateResizableColumns(newValue);
            }
        },
        // Set it to `borderless` when using the table in a container with no gutters.
        variant: {
            type: 'string',
            defaultValue: 'default',
            valid: ['default', 'borderless']
        },
        // class derived from size of available space
        __innerSizeClass: {
            type: 'string',
            defaultValue: ''
        },
        // pagination component
        __pagination: {
            type: 'object',
            onChange: getComponentSetter(ComponentFeature.PAGINATION)
        },
        // filtering component
        __filtering: {
            type: 'object',
            onChange: getComponentSetter(ComponentFeature.FILTERING)
        },
        // property filtering component
        __propertyFiltering: {
            type: 'object',
            onChange: getComponentSetter(ComponentFeature.PROPERTY_FILTERING)
        },
        // sorting component
        __sorting: {
            type: 'object',
            onChange: getComponentSetter(ComponentFeature.SORTING)
        },
        // selection component
        __selection: {
            type: 'object',
            onChange: getComponentSetter(ComponentFeature.SELECTION)
        },
        // preferences component
        __preferences: {
            type: 'object',
            onChange: function () {
                this.__updateToolsVisibility();
            }
        },
        // currenly visible columns
        __contentSelection: {
            type: 'object'
        }
    },
    callbacks: {
        initialized: function () {
            var _this = this;
            this.__columnDefinitionsReady = false;
            this.__itemsReady = false;
            this.__initializedFeatures = [];
            this.__preferencesSettings = {};
            this.__hideTools = false;
            this.__controller = new Controller(this, utilities);
            this.__name = nextGeneratedName();
            this.__shiftPressed = false;
            this.__stickyHandler = new StickyHeaderHandler(this.node, this.stickyHeaderVerticalOffset);
            var resizeSideEffect = function () {
                if (_this.stickyHeader) {
                    _this.__stickyHandler.updateColumnWidths();
                }
                _this.__updateScrollbarPosition();
            };
            this.__resizableColumnHandler = new ResizableColumnsHandler(this, resizeSideEffect);
        },
        attached: function () {
            var _a;
            var _this = this;
            this.__checkReadinessAndInitializeController();
            if (this.stickyHeader) {
                this.__stickyHandler.attach();
            }
            if (this.resizableColumns) {
                this.__resizableColumnHandler.attach();
            }
            _a = positionScrollbar(function () { return _this.node.querySelector('.awsui-scrollbar'); }, function () { return _this.node.querySelector('.awsui-scrollbar-content'); }, function () { return _this.node.querySelector('tbody'); }, function () { return _this.node.querySelector('table'); }, function () { return _this.node; }, 'awsui-scrollbar-stuck'), this.__updateScrollbarPosition = _a[0], this.__disposePositioner = _a[1];
        },
        detached: function () {
            if (this.stickyHeader) {
                this.__stickyHandler.detach();
            }
            if (this.resizableColumns) {
                this.__resizableColumnHandler.detach();
            }
            this.__disposePositioner();
        }
    },
    functions: {
        __getData: function () {
            return {
                items: this.items
            };
        },
        __checkReadinessAndInitializeController: checkReadinessAndInitializeController(['columnDefinitions', 'items']),
        __updateStickySetting: function (enabled) {
            if (this.__componentState === 'attached') {
                if (enabled) {
                    this.__stickyHandler.attach();
                }
                else {
                    this.__stickyHandler.detach();
                }
            }
        },
        __updateStickyVerticalOffset: function (value) {
            if (this.__stickyHandler) {
                this.__stickyHandler.setVerticalOffset(value);
            }
        },
        __updateResizableColumns: function (enabled) {
            if (this.__componentState === 'attached') {
                if (enabled) {
                    this.__resizableColumnHandler.attach();
                }
                else {
                    this.__resizableColumnHandler.detach();
                }
            }
        },
        __getModifiedColumndefinitions: function () {
            var columnDefinitions = this.columnDefinitions;
            if (!this.__contentSelection) {
                return columnDefinitions;
            }
            var modifiedColumnDefinitions = [];
            this.__contentSelection.forEach(function (id) {
                columnDefinitions.some(function (colDef) {
                    if (colDef.id === id) {
                        modifiedColumnDefinitions.push(colDef);
                        return true;
                    }
                });
            });
            return modifiedColumnDefinitions;
        },
        __updateToolsVisibility: function () {
            this.__hideTools = !(this.__preferences || this.__filtering || this.__propertyFiltering || this.__pagination);
        },
        __updateSelectionLabels: function () {
            // Selection labels are in the whole table (header and rows), so we need to
            // re-render the whole table.
            this.__update();
        },
        __updateWrapLines: function (newValue) {
            this.__controller.setWrapLines(newValue, false);
        },
        __fireWrapLinesChangeEvent: function () {
            var eventDetails = {
                value: this.wrapLines
            };
            this.__fireEvent('wrapLinesChange', eventDetails);
        },
        __fireContentSelectionChangeEvent: function () {
            var eventDetails = { contentSelection: this.__contentSelection };
            this.__fireEvent('contentSelectionChange', eventDetails);
        },
        __fireVisibleItemsChangeEvent: function () {
            var visibleItemsChangeDetail = {
                visibleItems: this.__controller.getVisibleItems()
            };
            this.__fireEvent('visibleItemsChange', visibleItemsChangeDetail);
        },
        __fireRowClickEvent: function (event, rowIndex, item) {
            var tableCell = findUpUntil(event.target, function (element) { return element.tagName === 'TD'; });
            if (!tableCell || !tableCell.classList.contains('awsui-table-selection-area')) {
                var rowClickDetails = {
                    rowIndex: rowIndex,
                    item: item
                };
                this.__fireEvent('rowClick', rowClickDetails);
            }
        },
        __fireContextMenuEvent: function (rowIndex, item, e) {
            var rowContextMenuDetails = {
                rowIndex: rowIndex,
                item: item,
                clientX: e.clientX,
                clientY: e.clientY
            };
            this.__fireEvent('rowContextMenu', rowContextMenuDetails, { nativeEvent: e });
        }
    },
    render: function (m, h) {
        var _this = this;
        var columnDefinitions = this.__getModifiedColumndefinitions();
        var controller = this.__controller;
        var visibleItems = controller.getVisibleItems();
        var isSelectionEnabled = !!this.__selection;
        var isSingleSelectionEnabled = isSelectionEnabled && this.__selection.selectionType !== 'multi';
        var componentDidUpdate = function () {
            // update has to be asynchronous, because render hook is called when not all changes are in the dom yet
            AwsUi.elementQueries.requestAnimationFrame(function () { return _this.__stickyHandler.updateColumnWidths(); });
            // update sticky scrollbar position when visibleItems change
            _this.__updateScrollbarPosition && _this.__prevItems !== visibleItems && _this.__updateScrollbarPosition();
            _this.__prevItems = visibleItems;
        };
        var getColumnStyle = function (columnDef, index, isSticky) {
            if (isSticky === void 0) { isSticky = false; }
            var isLastColumn = index === columnDefinitions.length - 1;
            var styleDeclarations = {};
            if (columnDef.minWidth) {
                // sticky header will get fixed widths copied into it, so we don't
                // want to apply the min values (plus, browser behaviour of table cells
                // with width & minWidth is inconsistent)
                if (!isSticky) {
                    styleDeclarations.minWidth = columnDef.minWidth;
                }
                // but we do want to keep maxWidth (as table cell with just width acts like minWidth)
                if (columnDef.allowLineWrap && !_this.resizableColumns) {
                    styleDeclarations.maxWidth = columnDef.minWidth;
                }
            }
            // Don't apply the width to the last column to make it automatically fill the remaining space
            if (columnDef.width && !isLastColumn) {
                styleDeclarations.width = columnDef.width + "px";
                if (!_this.resizableColumns) {
                    var width = columnDef.minWidth
                        ? Math.max(parseFloat(columnDef.minWidth), columnDef.width)
                        : columnDef.width;
                    // Apply the max-width to break unbreakable words, which will allow a column to have an exact specified width
                    styleDeclarations.maxWidth = width + "px";
                    // If the width is specified, we will apply min-width to force the column to have the exact specified width
                    styleDeclarations.minWidth = width + "px";
                }
            }
            return styleDeclarations;
        };
        var tableColumnHeader = function (columnDef, key, index) {
            if (columnDef.header) {
                return _this.renderToMithril("table-header-" + key + "-" + index, columnDef.header);
            }
            return '';
        };
        var tableHeaderCell = function (columnDef, key, index) {
            var resizer = (AwsUi.__createElement("div", { className: RESIZABLE_COLUMNS_RESIZER_CLASS, onmousedown: _this.__resizableColumnHandler.onMouseDown, onmouseenter: _this.__resizableColumnHandler.onMouseEnter, onmouseleave: _this.__resizableColumnHandler.onMouseLeave }));
            var sorting = _this.__sorting;
            var thAttributes = {
                style: getColumnStyle(columnDef, index, key === 'sticky')
            };
            var sortingData = {};
            if (sorting) {
                sortingData = {
                    sortingColumn: sorting.sortingColumn,
                    sortingDescending: sorting.sortingDescending,
                    disabled: sorting.disabled
                };
            }
            var headerAttr = {
                className: 'awsui-table-header-content'
            };
            if (columnDef.label) {
                headerAttr['aria-label'] = columnDef.label(sortingData);
            }
            if (_this.resizableColumns) {
                thAttributes['data-awsui-column-id'] = columnDef.id;
            }
            if (!controller.isColumnSortable(columnDef.id)) {
                return (AwsUi.__createElement("th", __assign({}, thAttributes),
                    AwsUi.__createElement("span", __assign({}, headerAttr), tableColumnHeader(columnDef, key, index)),
                    _this.resizableColumns && resizer));
            }
            var isSortingDisabled = sorting.disabled;
            var isColumnSorted = sorting.sortingColumn === columnDef.id;
            // The first click on not sorted column should set to false
            var nextSortingDescending = isColumnSorted && !sorting.sortingDescending;
            var onClick = function () {
                controller.processComponentChange(ComponentFeature.SORTING, {
                    sortingColumn: columnDef.id,
                    sortingDescending: nextSortingDescending,
                    sortableColumns: sorting.sortableColumns
                }, true);
            };
            thAttributes.className = new LegacyClassBuilder(_this)
                .addComponentClass('column-sortable')
                .addComponentClass('column-sorted', isColumnSorted)
                .addComponentClass("column-sorted-" + (nextSortingDescending ? 'asc' : 'desc'), isColumnSorted)
                .addComponentClass('column-sortable-disabled', isSortingDisabled)
                .addComponentClass('column-sortable-enabled', !isSortingDisabled)
                .toClassName();
            if (!isSortingDisabled) {
                thAttributes.onclick = onClick;
            }
            if (!isColumnSorted) {
                thAttributes['aria-sort'] = 'none';
            }
            else {
                thAttributes['aria-sort'] = nextSortingDescending ? 'ascending' : 'descending';
            }
            // Elements with role="button" do not have the default behavior of <button>, where pressing
            // Enter or Space will trigger a click event. Therefore we need to add this ourselves.
            // The native <button> element cannot be used due to a misaligned implementation in Firefox:
            // https://bugzilla.mozilla.org/show_bug.cgi?id=843003
            var onKeypress = function (e) {
                if (e.keyCode === KeyCode.enter || e.keyCode === KeyCode.space) {
                    e.preventDefault();
                    onClick();
                }
            };
            var buttonAttributes = __assign(__assign({}, headerAttr), { role: 'button', 'override-focus': true });
            if (isSortingDisabled) {
                buttonAttributes['aria-disabled'] = 'true';
            }
            else {
                buttonAttributes.tabindex = 0;
                buttonAttributes.onkeypress = onKeypress;
            }
            return (AwsUi.__createElement("th", __assign({}, thAttributes),
                AwsUi.__createElement("span", __assign({}, buttonAttributes),
                    tableColumnHeader(columnDef, key, index),
                    AwsUi.__createElement(Icon, { name: isColumnSorted ? (nextSortingDescending ? 'caret-up-filled' : 'caret-down-filled') : 'caret-down', className: "awsui-table-sorting-icon" })),
                _this.resizableColumns && resizer));
        };
        var selectionAriaLabels = function () {
            if (!isSelectionEnabled) {
                return null;
            }
            var labels = _this.__selection.__getSelectionLabels();
            var selectionData = _this.__selection.__getSelectionState();
            var name = _this.__name;
            var cb = new LegacyClassBuilder(_this).addComponentClass('selection-aria-labels').addClass('awsui-util-hide');
            return (AwsUi.__createElement("span", { className: cb.toClassName(), "aria-hidden": "true" },
                AwsUi.__createElement("span", { id: name + "-selection-group" }, labels.selectionGroupLabel),
                AwsUi.__createElement("span", { id: name + "-selection-all" }, labels.allItemsSelectionLabel(selectionData)),
                visibleItems.map(function (item, index) { return (AwsUi.__createElement("span", { key: name + "-selection-" + index, id: name + "-selection-" + index }, labels.itemSelectionLabel(selectionData, item))); })));
        };
        var selectionCellBaseProps = {
            name: this.__name,
            wrapper: 'td',
            baseClassName: 'awsui-table',
            selectionType: this.__selection && this.__selection.selectionType,
            onShiftToggle: function (shiftPressed) { return (_this.__shiftPressed = shiftPressed); }
        };
        var selectAllCell = function () {
            return isSingleSelectionEnabled ? (AwsUi.__createElement("td", { className: "awsui-table-selection-area", scope: "col" })) : (AwsUi.__createElement(SelectionControl, __assign({}, selectionCellBaseProps, { wrapperProps: { scope: 'col' }, itemId: "all", checked: controller.areAllVisibleItemsSelected(), disabled: !controller.isAnyVisibleItemSelectable(), indeterminate: controller.isSomeItemSelected(), onClick: function () { return controller.toggleAllVisibleItems(); }, onFocusUp: function () { return controller.moveFocus(-1, -1); }, onFocusDown: function () { return controller.moveFocus(-1, 1); } })));
        };
        var selectRowCell = function (item, index) { return (AwsUi.__createElement(SelectionControl, __assign({}, selectionCellBaseProps, { itemId: "" + index, checked: controller.isItemSelected(item), disabled: controller.isItemDisabled(item), onClick: function () { return controller.toggleItem(item, index, _this.__shiftPressed); }, onFocusUp: function () { return controller.moveFocus(index, -1); }, onFocusDown: function () { return controller.moveFocus(index, 1); } }))); };
        var tableBodyCell = function (item, columnDef, rowIndex, colIndex) {
            var content = '';
            if (columnDef.cell) {
                content = _this.renderToMithril("table-cell-" + rowIndex + "-" + colIndex, columnDef.cell, [{ $item: item }], 'span');
            }
            return AwsUi.__createElement("td", { style: getColumnStyle(columnDef, colIndex) }, content);
        };
        var noItems = function () {
            if (_this.loading) {
                return (AwsUi.__createElement("span", { className: "awsui-util-status-inactive" },
                    AwsUi.__createElement(Spinner, null),
                    " ",
                    _this.loadingText));
            }
            if (_this.items.length === 0) {
                return h.region('empty', 'span');
            }
            if (visibleItems.length === 0) {
                return h.region('noMatch', 'span');
            }
        };
        var tableBody = function () {
            if (_this.loading || _this.items.length === 0 || visibleItems.length === 0) {
                return (AwsUi.__createElement("tbody", null,
                    AwsUi.__createElement("tr", { className: "awsui-table-row awsui-table-row-merged" },
                        AwsUi.__createElement("td", { colspan: columnDefinitions.length + (isSelectionEnabled ? 1 : 0) }, noItems()))));
            }
            return (AwsUi.__createElement("tbody", null, visibleItems.map(function (item, rowIndex) {
                var isItemSelected = controller.isItemSelected(item);
                var cb = new LegacyClassBuilder(_this)
                    .addComponentClass('row')
                    .addComponentClass('row-selected', isItemSelected);
                return (AwsUi.__createElement("tr", { className: cb.toClassName(), onclick: function (event) { return _this.__fireRowClickEvent(event, rowIndex, item); }, oncontextmenu: function (e) { return _this.__fireContextMenuEvent(rowIndex, item, e); } },
                    isSelectionEnabled ? selectRowCell(item, rowIndex) : null,
                    columnDefinitions.map(function (def, coldIndex) { return tableBodyCell(item, def, rowIndex, coldIndex); })));
            })));
        };
        var tableHeader = function (key, active) {
            return (AwsUi.__createElement("thead", { className: active ? stickyActiveClass : '' },
                AwsUi.__createElement("tr", null,
                    isSelectionEnabled ? selectAllCell() : null,
                    columnDefinitions.map(function (def, index) { return tableHeaderCell(def, key, index); }))));
        };
        var tableClassName = new LegacyClassBuilder(this).addComponentClass('nowrap', !this.wrapLines).toClassName();
        var toolsCb = new LegacyClassBuilder(this)
            .addComponentClass('tools')
            .addComponentClass('tools-hidden', this.__hideTools)
            .addComponentClass('has-pagination', this.__pagination)
            .addComponentClass('has-property-filter', this.__propertyFiltering);
        var isRegionsContainerEmpty = !this.header && (!this.tools || this.__hideTools);
        var regionsContainerCb = new LegacyClassBuilder(this)
            .addComponentClass('regions-container')
            .addComponentClass('regions-container-empty', isRegionsContainerEmpty);
        var innerClassName = new LegacyClassBuilder(this)
            .addClass(RESIZABLE_COLUMNS_CLASS, this.resizableColumns)
            .addComponentClass('inner')
            .addClass(this.__innerSizeClass)
            .addPropertyValueClass('variant')
            .addClass(this.__stickyHandler.getCurrentStickyClass())
            .toClassName();
        var _a = syncedHandlers([
            function () { return _this.node.querySelector('.awsui-table-container'); },
            function () { return _this.node.querySelector('.awsui-scrollbar'); }
        ]), handleTableScroll = _a[0], handleStickyScroll = _a[1];
        return (AwsUi.__createElement("div", { className: innerClassName },
            AwsUi.__createElement("div", { className: "awsui-table-heading-strut" }),
            AwsUi.__createElement("div", { lifecycleKey: "awsui-table-header", className: "awsui-table-heading-container", componentDidMount: componentDidUpdate, componentDidUpdate: componentDidUpdate },
                AwsUi.__createElement("div", { className: regionsContainerCb.toClassName() },
                    h.region('header', 'div', { className: 'awsui-table-header' }),
                    h.region('tools', 'div', { className: toolsCb.toClassName() })),
                this.stickyHeader && (AwsUi.__createElement("div", { className: "awsui-table-header-copy " + tableClassName, "aria-hidden": "true" }, tableHeader('sticky', this.__stickyHandler.getCurrentStickyState() !== StickyState.DEFAULT)))),
            selectionAriaLabels(),
            AwsUi.__createElement("div", { className: "awsui-table-container", onscroll: function (event) {
                    _this.__stickyHandler.updateHorizontalPosition(event);
                    handleTableScroll();
                } },
                AwsUi.__createElement("table", { role: "table", className: tableClassName, componentDidMount: this.__stickyHandler.addItemsFocusListener, componentWillUnmount: this.__stickyHandler.removeItemsFocusListener, lifecycleKey: "awsui-table" },
                    tableHeader('main', this.__stickyHandler.getCurrentStickyState() === StickyState.DEFAULT),
                    tableBody()),
                AwsUi.__createElement("div", { className: RESIZABLE_COLUMNS_TRACKER_CLASS })),
            AwsUi.__createElement("div", { className: "awsui-scrollbar", onscroll: handleStickyScroll },
                AwsUi.__createElement("div", { className: "awsui-scrollbar-content" }))));
    }
});
export { utilities, Controller, ComponentFeature, arrayItemsEqual, getComponentSetter, updateController, checkReadinessAndInitializeController, SMALL_BREAKPOINT, StickyState, StickyHandler };
//# sourceMappingURL=index.js.map